import { useEffect, useState } from 'react'
import { Bold, Italic, Mic, RotateCcw, Trash2, X } from 'lucide-react'
import { useEditor } from '../store/editor'
import { ASPECTS, assetOf, clipEnd, confirmAspectChange, defaultColor, formatShort, maxClipDuration } from '../lib/model'
import type { Clip, ColorAdjust, Project } from '../types'
import { planOf, plansUrl } from '../lib/plan'

// ---- small controls ---------------------------------------------------------

function NumberField({ value, onCommit, step = 1, min, max, suffix }: {
  value: number; onCommit: (v: number) => void; step?: number; min?: number; max?: number; suffix?: string
}) {
  const [txt, setTxt] = useState(String(value))
  useEffect(() => setTxt(String(value)), [value])
  const commit = () => {
    let v = Number(txt.replace(',', '.'))
    if (!isFinite(v)) { setTxt(String(value)); return }
    if (min !== undefined) v = Math.max(min, v)
    if (max !== undefined) v = Math.min(max, v)
    onCommit(v)
  }
  return (
    <div style={{ position: 'relative' }}>
      <input className="num" value={txt} step={step} onChange={(e) => setTxt(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
        style={suffix ? { paddingRight: 18 } : undefined} />
      {suffix && <span className="hint" style={{ position: 'absolute', right: 6, top: 6 }}>{suffix}</span>}
    </div>
  )
}

function Slider({ label, value, min, max, step = 1, unit, onChange, onReset }: {
  label: string; value: number; min: number; max: number; step?: number
  unit?: '%' | 'x100'; onChange: (v: number, live: boolean) => void; onReset?: () => void
}) {
  const shown = unit ? Math.round(value * 100) : Math.round(value * 100) / 100
  return (
    <div className="row">
      <label onDoubleClick={onReset} title={onReset ? 'Doble clic para restablecer' : undefined}>{label}</label>
      <div className="slider">
        <input type="range" min={min} max={max} step={step} value={value}
          onPointerDown={() => useEditor.getState().beginGesture()}
          onChange={(e) => onChange(Number(e.target.value), true)} />
        <NumberField value={shown} suffix={unit === '%' ? '%' : undefined}
          onCommit={(v) => onChange(Math.max(min, Math.min(max, unit ? v / 100 : v)), false)} />
      </div>
    </div>
  )
}

// ---- helpers ----------------------------------------------------------------

function update(id: string, fn: (c: Clip, p: Project) => void, live: boolean) {
  useEditor.getState().mutate((p) => {
    const c = p.clips.find((x) => x.id === id)
    if (c) fn(c, p)
  }, { history: !live })
}

/** Change a clip's timeline length and ripple later clips on the same track. */
function setDuration(c: Clip, p: Project, dur: number) {
  const oldEnd = clipEnd(c)
  c.durationSec = Math.max(0.1, Math.min(dur, maxClipDuration(p, c)))
  if (c.kind === 'video' || c.kind === 'audio') c.sourceOutSec = c.sourceInSec + c.durationSec * c.speed
  else { c.sourceInSec = 0; c.sourceOutSec = c.durationSec }
  const delta = clipEnd(c) - oldEnd
  if (delta > 0) {
    for (const o of p.clips) if (o.id !== c.id && o.trackId === c.trackId && o.startSec >= oldEnd - 1e-3) o.startSec += delta
  }
}

function setSpeed(c: Clip, p: Project, speed: number) {
  const oldEnd = clipEnd(c)
  c.speed = Math.max(0.25, Math.min(4, speed))
  c.durationSec = (c.sourceOutSec - c.sourceInSec) / c.speed
  const delta = clipEnd(c) - oldEnd
  for (const o of p.clips) if (o.id !== c.id && o.trackId === c.trackId && o.startSec >= oldEnd - 1e-3) o.startSec += delta
}

// ---- main -------------------------------------------------------------------

export default function Inspector() {
  const sel = useEditor((s) => s.selection)
  const clip = useEditor((s) => (s.selection.length === 1 ? s.project.clips.find((c) => c.id === s.selection[0]) : undefined))
  return (
    <div className="panel right">
      <div className="insp">
        {sel.length > 1 ? <Multi n={sel.length} /> : clip ? <ClipInspector clip={clip} /> : <ProjectInspector />}
      </div>
    </div>
  )
}

function Multi({ n }: { n: number }) {
  return (
    <div className="group">
      <h4>{n} clips seleccionados</h4>
      <button className="btn danger" onClick={() => useEditor.getState().deleteSelected()}><Trash2 size={15} />Eliminar</button>
    </div>
  )
}

function ProjectInspector() {
  const settings = useEditor((s) => s.project.settings)
  const dur = useEditor((s) => s.project.durationSec)
  const n = useEditor((s) => s.project.clips.length)
  const origin = useEditor((s) => s.project.origin)
  const setS = (patch: Partial<typeof settings>) => useEditor.getState().mutate((p) => { p.settings = { ...p.settings, ...patch } })
  const setAspect = (w: number, h: number) => {
    if (confirmAspectChange(useEditor.getState().project, w, h)) setS({ width: w, height: h })
  }
  const restoreOrigin = () => {
    if (!origin) return
    const a = ASPECTS.find((x) => Math.abs(x.width / x.height - origin.width / origin.height) < 0.01)
    setS(a ? { width: a.width, height: a.height } : { width: origin.width, height: origin.height })
  }
  const originOk = !origin || Math.abs(origin.width / origin.height - settings.width / settings.height) < 0.01
  const user = useEditor((s) => s.user)
  const wmKey = origin ? `vaideo-wm-dismissed:${origin.uid}` : ''
  const [wmDismissed, setWmDismissed] = useState<string | null>(null)
  let wmHidden = wmDismissed === wmKey
  try { wmHidden ||= !!wmKey && localStorage.getItem(wmKey) === '1' } catch { /* storage blocked */ }
  const showWm = !!origin && !!user && planOf(user) === 'free' && !wmHidden
  const hideWm = () => {
    setWmDismissed(wmKey)
    try { localStorage.setItem(wmKey, '1') } catch { /* private mode */ }
  }
  return (
    <>
      {showWm && (
        <div className="group wm-notice" role="note">
          <button className="btn ghost icon sm wm-close" onClick={hideWm} title="Cerrar aviso" aria-label="Cerrar aviso"><X size={14} /></button>
          <p>Este vídeo se creó con el plan gratis y lleva la marca de Vaideo. Con Pro, tus vídeos salen sin marca.</p>
          <a href={plansUrl('editor-watermark')} target="_blank" rel="noopener">Ver planes</a>
        </div>
      )}
      {origin && (
        <div className={`group origin ${originOk ? '' : 'warn'}`}>
          <h4>Vídeo original de Vaideo</h4>
          <div className="origin-grid">
            <span>Formato</span><b>{origin.aspectRatio} · {origin.width >= origin.height ? 'Horizontal' : 'Vertical'}</b>
            <span>Resolución</span><b>{origin.width}×{origin.height} ({Math.min(origin.width, origin.height)}p)</b>
            <span>Fotogramas</span><b>{origin.fps} fps</b>
            {origin.language && <><span>Idioma</span><b>{origin.language === 'es' ? 'Español' : origin.language === 'en' ? 'English' : origin.language}</b></>}
          </div>
          {!originOk && (
            <>
              <div className="hint" style={{ color: 'var(--warn, #f5a524)' }}>
                El lienzo ({settings.width}×{settings.height}) no coincide con el formato original: el vídeo saldrá con bandas negras.
              </div>
              <button className="btn sm" onClick={restoreOrigin}>Volver a {origin.aspectRatio}</button>
            </>
          )}
        </div>
      )}
      <div className="group">
        <h4>Formato</h4>
        <div className="aspects">
          {ASPECTS.map((a) => {
            const on = Math.abs(a.width / a.height - settings.width / settings.height) < 0.01
            const isOrig = !!origin && Math.abs(a.width / a.height - origin.width / origin.height) < 0.01
            const k = 22 / Math.max(a.width, a.height)
            return (
              <button key={a.key} className={`aspect ${on ? 'on' : ''}`} title={isOrig ? `${a.hint} · formato original` : a.hint} onClick={() => setAspect(a.width, a.height)}>
                <span className="shape" style={{ width: a.width * k, height: a.height * k }} />
                {a.label}{isOrig && <small className="orig-tag">original</small>}
              </button>
            )
          })}
        </div>
        <div className="row"><label>Fondo</label>
          <input type="color" value={settings.backgroundColor.slice(0, 7)} onChange={(e) => setS({ backgroundColor: e.target.value })} />
        </div>
        <div className="row"><label>Fotogramas</label>
          <select className="sel" value={settings.fps} onChange={(e) => setS({ fps: Number(e.target.value) })}>
            {[24, 25, 30, 60].map((f) => <option key={f} value={f}>{f} fps</option>)}
          </select>
        </div>
        <div className="hint">Duración {formatShort(dur)} · {n} clips · {settings.width}×{settings.height}</div>
      </div>
      <div className="group">
        <h4>Atajos</h4>
        <div className="hint" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 10px' }}>
          <b>Espacio</b><span>Reproducir / pausar</span>
          <b>S</b><span>Dividir en el cursor</span>
          <b>Supr</b><span>Eliminar selección</span>
          <b>Ctrl+D</b><span>Duplicar</span>
          <b>Ctrl+Z / Y</b><span>Deshacer / rehacer</span>
          <b>← →</b><span>Fotograma (Mayús: 1 s)</span>
          <b>Ctrl+rueda</b><span>Zoom en la línea de tiempo</span>
          <b>Ctrl+S</b><span>Guardar</span>
        </div>
      </div>
    </>
  )
}

const KIND_LABEL: Record<Clip['kind'], string> = {
  video: 'Vídeo', audio: 'Audio', image: 'Imagen', text: 'Texto', subtitle: 'Subtítulo',
}

function ClipInspector({ clip }: { clip: Clip }) {
  const asset = useEditor((s) => assetOf(s.project, clip))
  const id = clip.id
  const visual = clip.kind === 'video' || clip.kind === 'image'
  const hasAudio = clip.kind === 'audio' || (clip.kind === 'video' && !!asset?.info.audioCodec)
  return (
    <>
      <div className="group">
        <h4>
          <span>{KIND_LABEL[clip.kind]}</span>
          <button className="btn ghost icon sm danger" title="Eliminar (Supr)" onClick={() => useEditor.getState().deleteSelected()}><Trash2 size={14} /></button>
        </h4>
        {asset && <div className="hint" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.name}</div>}
        <div className="row"><label>Duración</label>
          <NumberField value={Math.round(clip.durationSec * 100) / 100} min={0.1} step={0.1} suffix="s"
            onCommit={(v) => update(id, (c, p) => setDuration(c, p, v), false)} />
        </div>
      </div>

      {clip.kind === 'text' && <TextSection clip={clip} />}
      {clip.kind === 'subtitle' && <SubtitleSection clip={clip} />}

      {clip.kind !== 'audio' && (
        <div className="group">
          <h4>
            <span>Transformar</span>
            <button className="btn ghost sm" title="Restablecer" onClick={() => update(id, (c) => { c.transform = { x: 0, y: 0, scale: 1, rotation: 0, opacity: 1 } }, false)}><RotateCcw size={13} /></button>
          </h4>
          <Slider label="Escala" value={clip.transform.scale} min={0.05} max={4} step={0.01} unit="%"
            onChange={(v, live) => update(id, (c) => { c.transform.scale = v }, live)}
            onReset={() => update(id, (c) => { c.transform.scale = 1 }, false)} />
          <div className="row"><label>Posición</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <NumberField value={Math.round(clip.transform.x)} suffix="X" onCommit={(v) => update(id, (c) => { c.transform.x = v }, false)} />
              <NumberField value={Math.round(clip.transform.y)} suffix="Y" onCommit={(v) => update(id, (c) => { c.transform.y = v }, false)} />
            </div>
          </div>
          {visual && (
            <Slider label="Rotación" value={clip.transform.rotation} min={-180} max={180} step={1}
              onChange={(v, live) => update(id, (c) => { c.transform.rotation = v }, live)}
              onReset={() => update(id, (c) => { c.transform.rotation = 0 }, false)} />
          )}
          <Slider label="Opacidad" value={clip.transform.opacity} min={0} max={1} step={0.01} unit="%"
            onChange={(v, live) => update(id, (c) => { c.transform.opacity = v }, live)}
            onReset={() => update(id, (c) => { c.transform.opacity = 1 }, false)} />
          {visual && asset && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn sm" style={{ flex: 1 }} onClick={() => update(id, (c) => { c.transform = { ...c.transform, x: 0, y: 0, scale: 1 } }, false)}>Ajustar</button>
              <button className="btn sm" style={{ flex: 1 }} onClick={() => update(id, (c, p) => {
                const { width: W, height: H } = p.settings
                const nw = asset.info.width || W
                const nh = asset.info.height || H
                const cover = Math.max(W / nw, H / nh) / Math.min(W / nw, H / nh)
                c.transform = { ...c.transform, x: 0, y: 0, scale: Math.round(cover * 1000) / 1000 }
              }, false)}>Rellenar</button>
            </div>
          )}
        </div>
      )}

      {clip.kind === 'audio' && (
        <div className="group">
          <h4>Voz con IA</h4>
          <div className="hint">Cambia el texto o la voz y genera una locución nueva; los subtítulos se sincronizan solos.</div>
          <button className="btn sm" onClick={() => useEditor.getState().set({ dialog: 'voice' })}><Mic size={14} />Regenerar voz</button>
        </div>
      )}

      {(clip.kind === 'video' || clip.kind === 'audio') && (
        <div className="group">
          <h4>Velocidad</h4>
          <div className="chips">
            {[0.5, 1, 1.5, 2, 3].map((v) => (
              <button key={v} className={`chip ${Math.abs(clip.speed - v) < 1e-3 ? 'on' : ''}`} onClick={() => update(id, (c, p) => setSpeed(c, p, v), false)}>{v}x</button>
            ))}
          </div>
          <Slider label="Personalizada" value={clip.speed} min={0.25} max={4} step={0.05}
            onChange={(v, live) => update(id, (c, p) => setSpeed(c, p, v), live)} />
        </div>
      )}

      {(hasAudio || visual) && (
        <div className="group">
          <h4>{hasAudio ? 'Audio y fundidos' : 'Fundidos'}</h4>
          {hasAudio && (
            <>
              <Slider label="Volumen" value={clip.audio.volume} min={0} max={2} step={0.01} unit="%"
                onChange={(v, live) => update(id, (c) => { c.audio.volume = v; if (v > 0) c.audio.muted = false }, live)}
                onReset={() => update(id, (c) => { c.audio.volume = 1 }, false)} />
              <label className="check"><input type="checkbox" checked={clip.audio.muted}
                onChange={(e) => update(id, (c) => { c.audio.muted = e.target.checked }, false)} />Silenciar</label>
            </>
          )}
          <Slider label="Entrada" value={clip.audio.fadeInSec} min={0} max={Math.min(5, clip.durationSec / 2)} step={0.1}
            onChange={(v, live) => update(id, (c) => { c.audio.fadeInSec = v }, live)} />
          <Slider label="Salida" value={clip.audio.fadeOutSec} min={0} max={Math.min(5, clip.durationSec / 2)} step={0.1}
            onChange={(v, live) => update(id, (c) => { c.audio.fadeOutSec = v }, live)} />
        </div>
      )}

      {visual && <ColorSection clip={clip} />}
    </>
  )
}

function TextSection({ clip }: { clip: Clip }) {
  const t = clip.text!
  const id = clip.id
  const setT = (patch: Partial<typeof t>, live = false) => update(id, (c) => { c.text = { ...c.text!, ...patch } }, live)
  return (
    <div className="group">
      <h4>Texto</h4>
      <textarea className="area" value={t.text} onFocus={() => useEditor.getState().beginGesture()}
        onChange={(e) => setT({ text: e.target.value }, true)} />
      <Slider label="Tamaño" value={t.fontSize} min={16} max={300} step={1}
        onChange={(v, live) => setT({ fontSize: v }, live)} />
      <div className="row"><label>Estilo</label>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className={`btn icon sm ${t.bold ? 'active' : ''}`} onClick={() => setT({ bold: !t.bold })} title="Negrita"><Bold size={14} /></button>
          <button className={`btn icon sm ${t.italic ? 'active' : ''}`} onClick={() => setT({ italic: !t.italic })} title="Cursiva"><Italic size={14} /></button>
        </div>
      </div>
      <div className="row"><label>Color</label>
        <input type="color" value={t.color.slice(0, 7)} onChange={(e) => setT({ color: e.target.value })} />
      </div>
      <div className="row"><label>Fondo</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={t.backgroundColor !== 'transparent'}
            onChange={(e) => setT({ backgroundColor: e.target.checked ? '#000000cc' : 'transparent' })} />
          {t.backgroundColor !== 'transparent' && (
            <input type="color" value={t.backgroundColor.slice(0, 7)} onChange={(e) => setT({ backgroundColor: e.target.value + (t.backgroundColor.length === 9 ? t.backgroundColor.slice(7) : '') })} />
          )}
        </div>
      </div>
    </div>
  )
}

function SubtitleSection({ clip }: { clip: Clip }) {
  const id = clip.id
  return (
    <div className="group">
      <h4>Subtítulo</h4>
      <textarea className="area" value={clip.rawText ?? ''} onFocus={() => useEditor.getState().beginGesture()}
        onChange={(e) => update(id, (c) => { c.rawText = e.target.value }, true)} />
      <div className="row"><label>Posición</label>
        <div className="seg">
          <button className={clip.positionMode !== 'free' ? 'on' : ''} onClick={() => update(id, (c) => { c.positionMode = 'preset'; c.transform.y = 0 }, false)}>Global</button>
          <button className={clip.positionMode === 'free' ? 'on' : ''} onClick={() => update(id, (c, p) => {
            if (c.positionMode === 'free') return
            const f = p.subtitleStyle.position === 'top' ? 0.1 : p.subtitleStyle.position === 'middle' ? 0.5 : 0.88
            c.positionMode = 'free'
            c.transform.y = Math.round(p.settings.height * (f - 0.5))
          }, false)}>Libre</button>
        </div>
      </div>
      <button className="btn sm" onClick={() => useEditor.getState().set({ leftTab: 'subtitles' })}>Editar estilo de todos los subtítulos</button>
    </div>
  )
}

const COLOR_FIELDS: { key: keyof ColorAdjust; label: string; min: number; max: number; step: number }[] = [
  { key: 'brightness', label: 'Brillo', min: -1, max: 1, step: 0.01 },
  { key: 'contrast', label: 'Contraste', min: -1, max: 1, step: 0.01 },
  { key: 'saturation', label: 'Saturación', min: -1, max: 1, step: 0.01 },
  { key: 'exposure', label: 'Exposición', min: -1, max: 1, step: 0.01 },
  { key: 'temperature', label: 'Temperatura', min: -1, max: 1, step: 0.01 },
  { key: 'hue', label: 'Tono', min: -180, max: 180, step: 1 },
]

function ColorSection({ clip }: { clip: Clip }) {
  const id = clip.id
  return (
    <div className="group">
      <h4>
        <span>Ajustes de color</span>
        <button className="btn ghost sm" title="Restablecer" onClick={() => update(id, (c) => { c.color = defaultColor() }, false)}><RotateCcw size={13} /></button>
      </h4>
      {COLOR_FIELDS.map((f) => (
        <Slider key={f.key} label={f.label} value={clip.color[f.key]} min={f.min} max={f.max} step={f.step}
          unit={f.key === 'hue' ? undefined : 'x100'}
          onChange={(v, live) => update(id, (c) => { c.color[f.key] = v }, live)}
          onReset={() => update(id, (c) => { c.color[f.key] = 0 }, false)} />
      ))}
    </div>
  )
}
